import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Store } from '../../../store';
import { Upload3, RetryIcon } from '../../../../src/assets/images';
import {
    getActiveConversationChatId,
    getDownloadFileName,
    uploadFileFailure,
    uploadFileSuccess,
} from '../../../Helpers/Chat/ChatHelper';
import { CancelMediaDownload, CancelMediaUpload, RetryMediaUpload } from '../../../Actions/ChatHistory';
import SDK from '../../SDK';
import { DownloadingChatMedia, MediaDownloadDataAction } from '../../../Actions/Media';
import logger from '../../../Helpers/logger';
import SendingFailed from './SendingFailed';
import CircularProgress from './CircularProgress';

// Upload Status
// 0 - Before Upload
// 1 - Uploading Loader - Sender
// 2 - Uploaded
// 3 - Upload Failed
// 4 - Download Loader - Receiver
// 5 - File Not Available
// 6 - Receiver Download Failed
// 7 - Sender - User Cancelled
// 8 - Forward Uploading
// 9 - User Cancelled Downloading
// 10 - Resuming file upload
// 11 - Resuming file upload in queue

function ProgressLoader(props = {}) {
    const {
        msgId = '',
        file_url = '',
        isSender = true,
        uploadStatus = 0,
        imgFileDownloadOnclick, //click
        messageType,
        downloadEnabled,
        isUploadingCancelled,
    } = props;

    const globalStore = useSelector(state => state);
    const {
        appOnlineStatus: { isOnline = true } = {},
        mediaUploadData: { data = {} } = {},
        mediaDownloadingData,
        mediaDownloadData,
    } = globalStore || {};

    const getAnimateClass = () =>
        !data[msgId]?.progress || data[msgId]?.progress < 0 || uploadStatus === 0 || uploadStatus === 11
            ? 'progress-animate'
            : '';
    const getDownloadAnimateClass = () =>
        mediaDownloadingData?.downloadingData[msgId]?.progress > 0 && uploadStatus !== 11
            ? 'progress-animate active-progress'
            : '';
    const getActiveProgressClass = () => (uploadStatus === 1 && data[msgId]?.progress > 0 ? 'active-progress' : '');
    const mediauploadStatus = mediaDownloadingData?.downloadingData[msgId]?.uploadStatus;
    const progress = mediaDownloadingData?.downloadingData[msgId]?.progress;

    let progressBarClass;
    if (mediauploadStatus !== 9) {
        if (!progress || progress < 0) {
            progressBarClass = 'progressBar progress-animate';
        } else {
            progressBarClass = 'progressBar active-progress';
        }
    }

    useEffect(() => {
        if (!isOnline) {
            progressBarClass = '';
        }
    }, [isOnline]);

    const cancelMediaUploadDownload = async () => {
        try {
            if (uploadStatus === 8) return true;
            if (data[msgId] && !mediaDownloadData?.downloadingStatus[msgId]?.downloading) {
                const canceledres = await SDK.cancelMediaUpload(msgId);
                if (
                    canceledres?.statusCode === 200 &&
                    (uploadStatus === 0 || uploadStatus === 1 || uploadStatus === 11)
                ) {
                    const cancelObj = {
                        msgId,
                        fromUserId: getActiveConversationChatId(),
                        uploadStatus: 7,
                        mediaUploading: true,
                        mediaUploadCanceled: true,
                    };
                    Store.dispatch(CancelMediaUpload(cancelObj));
                }
            } else if (mediaDownloadingData?.downloadingData[msgId]?.msgId === msgId) {
                if (mediaDownloadingData?.downloadingData[msgId]?.uploadStatus !== 9) {
                    const downloadCanceRes = SDK.cancelMediaDownload(msgId);
                    if (downloadCanceRes.statusCode === 200) {
                        const cancelDownloadingObj = {
                            msgId,
                            uploadStatus: 9,
                        };
                        Store.dispatch(CancelMediaDownload(cancelDownloadingObj));
                    }
                } else {
                    await SDK.downloadMedia(
                        msgId,
                        _res => {
                            const resumeDownloadingObj = {
                                msgId,
                                uploadStatus: 1,
                            };
                            Store.dispatch(MediaDownloadDataAction(resumeDownloadingObj));
                        },
                        mediaResponse => {
                            const { downloadingFile, downloadingMediaType } =
                                mediaDownloadData?.downloadingStatus[msgId] || {};
                            const fileName =
                                downloadingMediaType === 'file'
                                    ? downloadingFile
                                    : getDownloadFileName(downloadingFile, downloadingMediaType);
                            let fileUrl = mediaResponse.blobUrl;
                            const anchor = document.createElement('a');
                            anchor.style.display = 'none';
                            anchor.href = fileUrl;
                            anchor.setAttribute('download', fileName);
                            document.body.appendChild(anchor);
                            anchor.click();
                            window.URL.revokeObjectURL(anchor.href);
                            document.body.removeChild(anchor);
                            Store.dispatch(
                                Store.dispatch(DownloadingChatMedia({ downloadMediaMsgId: msgId, downloading: false })),
                            );
                            Store.dispatch(MediaDownloadDataAction({ msgId, progress: null }));
                        },
                        error => {
                            logger.debug('error in downloading media file', error);
                        },
                    );
                }
            } else {
                const cancelObj = {
                    msgId,
                    fromUserId: getActiveConversationChatId(),
                    uploadStatus: 3,
                    mediaUploading: true,
                    mediaUploadCanceled: true,
                };
                Store.dispatch(CancelMediaUpload(cancelObj));
            }

            return false;
        } catch (error) {
            logger.error('Error in cancelMediaUploadDownload:', error);
        }
    };

    const retryMediaUpload = async () => {
        try {
            if (data[msgId]?.mediaUploadCanceled) {
                if (data[msgId].mediaUploading) {
                    if (msgId) {
                        const retryObj = {
                            msgId,
                            fromUserId: getActiveConversationChatId(),
                            uploadStatus: 11,
                        };
                        Store.dispatch(RetryMediaUpload(retryObj));
                    }
                    SDK.resumeMediaUpload(
                        msgId,
                        () => {
                            if (data[msgId]) {
                                const resumeDatalObj = {
                                    msgId,
                                    fromUserId: getActiveConversationChatId(),
                                    uploadStatus: 10,
                                    mediaUploadCanceled: false,
                                };
                                Store.dispatch(CancelMediaUpload(resumeDatalObj));
                            }
                        },
                        response => {
                            uploadFileSuccess(msgId, getActiveConversationChatId(), response);
                        },
                        _error => {
                            uploadFileFailure(msgId, getActiveConversationChatId());
                        },
                    );
                }
            }
            return false;
        } catch (error) {
            logger.error('Error in cancelMediaUpload:', error);
            // Handle the error as needed
        }
    };

    const handleMediaClick = e => {
        if (isSender) {
            imgFileDownloadOnclick(e);
        } else {
            retryMediaUpload();
        }
    };

    const commonRetryAction = () => {
        return (
            <div className="image-error-overlay">
                <div
                    id={file_url}
                    className="retry-image-button"
                    onClick={handleMediaClick}
                    data-jest-id={'jesthandleMediaClick'}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleMediaClick();
                        }
                    }}
                    aria-label="Retry loading image"
                    role="button"
                    tabIndex="0"
                >
                    <RetryIcon />
                </div>
            </div>
        );
    };

    const getSyncClass = () => (isSender ? 'sync left' : 'sync right');
    const progressView = () => {
        return (
            <div className={`loadingProgress ${getSyncClass()}`}>
                <Upload3 />
                <span className="progressBar progress-animate" style={{ width: '50%' }}></span>
            </div>
        );
    };
    const progressViewdiffer = () => {
        progressView();
    };

    const getProgressValue = () => {
        const uploadProgress = data[msgId]?.progress;
        const downloadProgress = mediaDownloadingData?.downloadingData[msgId]?.progress;
        return uploadProgress || downloadProgress || 0;
    };

    const shouldShowCancelButton = () => {
        const shouldDisableAction =
            ((messageType === 'image' || messageType === 'audio') && !downloadEnabled && isSender) ||
            (messageType === 'audio' && !isSender && !data[msgId]);
        return !shouldDisableAction;
    };

    return (
        <React.Fragment>
            <div className="progressOverlay">
                {isOnline &&
                (uploadStatus === 1 ||
                    uploadStatus === 0 ||
                    uploadStatus === 8 ||
                    uploadStatus === 9 ||
                    uploadStatus === 11) ? (
                    <div className="circular-progress-container">
                        {shouldShowCancelButton() && (
                            <button
                                className="progress-cancel-button"
                                onClick={cancelMediaUploadDownload}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        cancelMediaUploadDownload();
                                    }
                                }}
                                aria-label="Cancel upload"
                                data-jest-id={'jestcancelMediaUpload'}
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M6 6L18 18M6 18L18 6"
                                        stroke="#fff"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </button>
                        )}
                        <div className="circular-spinner-wrapper">
                            <CircularProgress progress={getProgressValue()} size={56} strokeWidth={4} />
                        </div>
                    </div>
                ) : null}

                {(msgId === mediaDownloadData?.downloadingStatus[msgId]?.downloadMediaMsgId &&
                    mediaDownloadData?.downloadingStatus[msgId]?.downloading === true) ||
                (msgId === mediaDownloadingData?.downloadingData[msgId]?.msgId &&
                    mediaDownloadingData?.downloadingData[msgId]?.uploadStatus === 9) ? (
                    <div className="circular-progress-container">
                        {!((messageType === 'image' || messageType === 'audio') && !downloadEnabled) && (
                            <button
                                className="progress-cancel-button"
                                onClick={cancelMediaUploadDownload}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        cancelMediaUploadDownload();
                                    }
                                }}
                                aria-label="Cancel download"
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M6 6L18 18M6 18L18 6"
                                        stroke="#fff"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </button>
                        )}
                        <div className="circular-spinner-wrapper">
                            <CircularProgress
                                progress={mediaDownloadingData?.downloadingData[msgId]?.progress || 0}
                                size={56}
                                strokeWidth={4}
                            />
                        </div>
                    </div>
                ) : null}

                {/* Don't show retry UI for images with uploadStatus 3 - ImageComponent handles it */}
                {(uploadStatus === 3 || isUploadingCancelled) && commonRetryAction()}
                {uploadStatus === 4 && data[msgId]?.mediaUploadCanceled && commonRetryAction()}
                {uploadStatus === 4 && isOnline ? progressViewdiffer() : null}

                {uploadStatus === 5 && (
                    <div className="reLoadFile failed">
                        <span className="failed-text">File doesn't exists</span>
                    </div>
                )}

                {(uploadStatus === 6 ||
                    ((uploadStatus === 4 || uploadStatus === 1 || uploadStatus === 11 || uploadStatus === 0) &&
                        !isOnline)) &&
                    commonRetryAction()}
            </div>

            {/* Don't show SendingFailed for images with uploadStatus 3 - ImageComponent handles it */}
            {uploadStatus === 3 && !isSender && isUploadingCancelled && <SendingFailed />}
            {(uploadStatus === 1 || uploadStatus === 11 || uploadStatus === 0) && !isOnline && <SendingFailed />}
            {uploadStatus === 4 && data[msgId]?.mediaUploadCanceled && <SendingFailed />}
        </React.Fragment>
    );
}

export default ProgressLoader;

ProgressLoader.propTypes = {
    msgId: PropTypes.string,
    file_url: PropTypes.string,
    isSender: PropTypes.bool,
    uploadStatus: PropTypes.number,
    imgFileDownloadOnclick: PropTypes.func,
    messageType: PropTypes.string,
    downloadEnabled: PropTypes.bool,
    isUploadingCancelled: PropTypes.bool,
};
